import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import NavBar from '../NavBarPage';
import '@/styles/permissionsStyles.scss';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FaExclamationTriangle } from 'react-icons/fa';
import * as Sentry from '@sentry/react';

/**
 * UserPermissionsPage Component
 * 
 * Enhanced permissions management with:
 * - Category grouping with collapsible sections
 * - Select All functionality by category
 * - Role templates for quick permission assignment
 * - Visual distinction between role-inherited and user-specific permissions
 * - Improved search and filtering
 */
const UserPermissionsPage = () => {

    // State for roles management
    const [newRoleName, setNewRoleName] = useState('');
    const [similarRoles, setSimilarRoles] = useState([]);
    const roleDebounceRef = useRef(null);
    const [roles, setRoles] = useState([]);
    const [selectedRole, setSelectedRole] = useState(null);
    const [rolesCount, setRoleCounts] = useState({});
    const [searchRolesTerm, setSearchRolesTerm] = useState('');
    
    // State for processes/permissions
    const [processes, setProcesses] = useState([]);
    const [searchProcessesTerm, setSearchProcessesTerm] = useState('');
    
    // State for users management
    const [users, setUsers] = useState([]);
    const [userToProcessMappings, setUserToProcessMappings] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [searchUsersTerm, setSearchUsersTerm] = useState('');
    const [searchUserProcessesTerm, setSearchUserProcessesTerm] = useState('');
    const [roleProcessesMappings, setRoleProcessesMappings] = useState([]);
    
    // UI state
    const [activeTab, setActiveTab] = useState("roles");
    const [isLoading, setIsLoading] = useState();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isRoleChangeModalOpen, setIsRoleChangeModalOpen] = useState(false);
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
    
    // State for collapsible categories - tracks which categories are expanded
    const [expandedCategories, setExpandedCategories] = useState({});
    
    // Auth and navigation
    const { apiFetch, logout, refreshPermissions } = useAuth();
    const [sessionExpired, setSessionExpired] = useState(false);
    const navigate = useNavigate();

    /**
     * Role templates for quick permission assignment
     * Each template defines a preset configuration for common role types
     */
    const roleTemplates = [
        {
            name: 'Full Admin',
            description: 'Grant all permissions with full access (no enquiry-only restrictions)',
            icon: '👑',
            apply: (processes) => processes.map(p => ({ ...p, grantAccess: true, enquiryOnly: false }))
        },
        {
            name: 'Read Only',
            description: 'Grant all permissions but with enquiry-only access',
            icon: '👁️',
            apply: (processes) => processes.map(p => ({ ...p, grantAccess: true, enquiryOnly: true }))
        },
        {
            name: 'Clear All',
            description: 'Remove all permissions',
            icon: '🗑️',
            apply: (processes) => processes.map(p => ({ ...p, grantAccess: false, enquiryOnly: false }))
        }
    ];

    /**
     * Groups processes by their category for organized display
     * Returns an object with category names as keys and arrays of processes as values
     */
    const groupedProcesses = useMemo(() => {
        const groups = {};
        processes.forEach(process => {
            const category = process.location || 'Uncategorized';
            if (!groups[category]) {
                groups[category] = [];
            }
            groups[category].push(process);
        });
        // Sort categories alphabetically
        return Object.keys(groups)
            .sort()
            .reduce((acc, key) => {
                acc[key] = groups[key].sort((a, b) => a.description.localeCompare(b.description));
                return acc;
            }, {});
    }, [processes]);

    /**
     * Filtered and grouped processes based on search term (for Roles tab)
     */
    const filteredGroupedProcesses = useMemo(() => {
        if (!searchProcessesTerm.trim()) {
            return groupedProcesses;
        }
        const searchLower = searchProcessesTerm.toLowerCase();
        const filtered = {};
        Object.keys(groupedProcesses).forEach(category => {
            const matchingProcesses = groupedProcesses[category].filter(p =>
                p.location.toLowerCase().includes(searchLower) ||
                p.description.toLowerCase().includes(searchLower)
            );
            if (matchingProcesses.length > 0) {
                filtered[category] = matchingProcesses;
            }
        });
        return filtered;
    }, [groupedProcesses, searchProcessesTerm]);

    /**
     * Filtered and grouped processes for User Permissions tab (uses different search state)
     */
    const filteredUserGroupedProcesses = useMemo(() => {
        if (!searchUserProcessesTerm.trim()) {
            return groupedProcesses;
        }
        const searchLower = searchUserProcessesTerm.toLowerCase();
        const filtered = {};
        Object.keys(groupedProcesses).forEach(category => {
            const matchingProcesses = groupedProcesses[category].filter(p =>
                p.location.toLowerCase().includes(searchLower) ||
                p.description.toLowerCase().includes(searchLower)
            );
            if (matchingProcesses.length > 0) {
                filtered[category] = matchingProcesses;
            }
        });
        return filtered;
    }, [groupedProcesses, searchUserProcessesTerm]);

    /**
     * Initialize all categories as expanded on first load
     */
    useEffect(() => {
        if (Object.keys(groupedProcesses).length > 0 && Object.keys(expandedCategories).length === 0) {
            const initialExpanded = {};
            Object.keys(groupedProcesses).forEach(cat => {
                initialExpanded[cat] = true;
            });
            setExpandedCategories(initialExpanded);
        }
    }, [groupedProcesses]);

    /**
     * Toggles a category's expanded/collapsed state
     */
    const toggleCategory = (category) => {
        setExpandedCategories(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    /**
     * Expands all categories
     */
    const expandAllCategories = () => {
        const allExpanded = {};
        Object.keys(groupedProcesses).forEach(cat => {
            allExpanded[cat] = true;
        });
        setExpandedCategories(allExpanded);
    };

    /**
     * Collapses all categories
     */
    const collapseAllCategories = () => {
        const allCollapsed = {};
        Object.keys(groupedProcesses).forEach(cat => {
            allCollapsed[cat] = false;
        });
        setExpandedCategories(allCollapsed);
    };

    /**
     * Selects all permissions within a specific category
     */
    const selectAllInCategory = (category, grantAccess) => {
        setProcesses(prevProcesses =>
            prevProcesses.map(p =>
                p.location === category
                    ? { ...p, grantAccess: grantAccess, enquiryOnly: grantAccess ? p.enquiryOnly : false }
                    : p
            )
        );
    };

    /**
     * Sets enquiry-only mode for all permissions within a specific category
     */
    const setEnquiryOnlyInCategory = (category, enquiryOnly) => {
        setProcesses(prevProcesses =>
            prevProcesses.map(p =>
                p.location === category && p.grantAccess
                    ? { ...p, enquiryOnly: enquiryOnly }
                    : p
            )
        );
    };

    /**
     * Checks if all processes in a category are selected
     */
    const isCategoryAllSelected = (category) => {
        const categoryProcesses = groupedProcesses[category] || [];
        return categoryProcesses.length > 0 && categoryProcesses.every(p => p.grantAccess);
    };

    /**
     * Checks if some (but not all) processes in a category are selected
     */
    const isCategorySomeSelected = (category) => {
        const categoryProcesses = groupedProcesses[category] || [];
        const selectedCount = categoryProcesses.filter(p => p.grantAccess).length;
        return selectedCount > 0 && selectedCount < categoryProcesses.length;
    };

    /**
     * Gets count statistics for a category
     */
    const getCategoryStats = (category) => {
        const categoryProcesses = groupedProcesses[category] || [];
        const selected = categoryProcesses.filter(p => p.grantAccess).length;
        const enquiryOnly = categoryProcesses.filter(p => p.grantAccess && p.enquiryOnly).length;
        return { total: categoryProcesses.length, selected, enquiryOnly };
    };

    /**
     * Applies a role template to current permissions
     */
    const applyRoleTemplate = (template) => {
        setProcesses(template.apply(processes));
        setIsTemplateModalOpen(false);
        toast.info(`Applied "${template.name}" template. Don't forget to save!`);
    };

    const fetchRoles = async () => { //Fetches the roles from the database
        try {
            setIsLoading(true);
            const response = await apiFetch("/api/Permissions/roles");

            const data = await response.json();
            if (data.success) {
                setRoles(data.data);
            } else {
                toast.error("Failed to fetch roles.");
            }
        } catch (e) {
            Sentry.captureException(e);
            if (e.message && e.message.includes('Session expired')) {
                setSessionExpired(true);
                logout();
                return;
            }
            console.error(e);
            toast.error("An error occurred while fetching roles.");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchActiveUsers = async () => {
        try {
            const response = await apiFetch("/api/Permissions/active-users");

            const data = await response.json();
            if (data.success) {
                setUsers(data.data); // Update the `users` state
            } else {
                toast.error("Failed to fetch active users.");
            }
        } catch (e) {
            Sentry.captureException(e);
            if (e.message && e.message.includes('Session expired')) {
                setSessionExpired(true);
                logout();
                return;
            }
            console.error(e);
            toast.error("An error occurred while fetching active users.");
        }
    };

    const fetchProcessesForRole = async (roleID) => {
        try {
            setIsLoading(true);

            // Fetch all processes
            const processesResponse = await apiFetch("/api/Permissions/processes");
            if (!processesResponse.ok) {
                toast.error("Failed to fetch processes.");
                return;
            }
            const processesData = await processesResponse.json();

            // Fetch role-to-process mappings for the selected role
            const roleProcessesResponse = await apiFetch(`/api/Permissions/all-role-processes`);
            if (!roleProcessesResponse.ok) {
                toast.error("Failed to fetch role processes.");
                return;
            }
            const roleProcessesData = await roleProcessesResponse.json();

            setRoleProcessesMappings(roleProcessesData.data);

            const roleProcesses = roleProcessesData.data.filter(rtp => rtp.roleID === roleID);

            // Map processes with role-specific permissions
            const processesWithPermissions = processesData.data.map((process) => {
                const matchingRoleProcess = roleProcesses.find(rp => rp.processID === process.processID);
                return matchingRoleProcess
                    ? {
                        ...process,
                        grantAccess: true,
                        enquiryOnly: matchingRoleProcess.enquiryOnly,
                    }
                    : { ...process, grantAccess: false, enquiryOnly: false };
            });

            setProcesses(processesWithPermissions);
        } catch (e) {
            Sentry.captureException(e);
            if (e.message && e.message.includes('Session expired')) {
                setSessionExpired(true);
                logout();
                return;
            }
            console.error(e);
            toast.error("An error occurred while fetching processes for the role.");
        } finally {
            setIsLoading(false);
        }
    };

    const updateUserRole = async (userId, roleId) => {
        try {
            const response = await apiFetch(`/api/Permissions/update-user-role/${userId}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(roleId),
            });

            const data = await response.json();

            if (data.success) {
                toast.success("User role updated successfully.");
                fetchActiveUsers(); // Refresh the user list
            } else {
                toast.error("Failed to update user role.");
            }
        } catch (e) {
            Sentry.captureException(e);
            if (e.message && e.message.includes('Session expired')) {
                setSessionExpired(true);
                logout();
                return;
            }
            console.error(e);
            toast.error("An error occurred while updating user role.");
        }
    };

    const handleRoleClick = (roleID) => {
        setSelectedRole(roleID);
        fetchProcessesForRole(roleID);
    };

    const handleRoleProcessChange = (processID, field, value) => {
        setProcesses(prevProcesses =>
            prevProcesses.map(p =>
                p.processID === processID
                    ? { ...p, [field]: value }
                    : p
            )
        );
    };

    const handleSave = async () => {
        if (!selectedRole) {
            toast.error("Please select a role first.");
            return;
        }

        try {
            setIsLoading(true);

            const roleToProcessData = processes
                .filter((process) => process.grantAccess) // Only include processes with grantAccess = true
                .map((process) => ({
                    roleID: selectedRole,
                    processID: process.processID,
                    enquiryOnly: process.enquiryOnly,
                }));

            const response = await apiFetch("/api/Permissions/save-role-processes", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(roleToProcessData),
            });

            const data = await response.json();

            if (data.success) {
                toast.success("Permissions saved successfully.");
                await refreshPermissions(); // Refresh permissions for current user
            } else {
                toast.error("Failed to save permissions.");
            }
        } catch (e) {
            Sentry.captureException(e);
            if (e.message && e.message.includes('Session expired')) {
                setSessionExpired(true);
                logout();
                return;
            }
            console.error(e);
            toast.error("An error occurred while saving permissions.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveUserPermissions = async () => {
        if (!selectedUser) {
            toast.error("Please select a user first.");
            return;
        }

        try {
            setIsLoading(true);
            const userToProcessData = processes
                .filter(process => process.grantAccess && process.fromUser)
                .map(process => ({
                    userID: selectedUser,
                    processID: process.processID,
                    enquiryOnly: process.enquiryOnly
                }));

            const response = await apiFetch(`/api/Permissions/save-user-process/${selectedUser}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(userToProcessData),
            });

            const data = await response.json();

            if (data.success) {
                toast.success("User permissions saved successfully.");
                const updatedMappingsResponse = await apiFetch("/api/Permissions/all-user-processes");
                if (updatedMappingsResponse.ok) {
                    const mappingsData = await updatedMappingsResponse.json();
                    if (mappingsData.success) {
                        setUserToProcessMappings(mappingsData.data);
                    }
                }
                await refreshPermissions(); // Refresh permissions for current user
            } else {
                toast.error("Failed to save user permissions.");
            }
        } catch (e) {
            Sentry.captureException(e);
            if (e.message && e.message.includes('Session expired')) {
                setSessionExpired(true);
                logout();
                return;
            }
            console.error(e);
            toast.error("An error occurred while saving user permissions.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchRoles(); // Fetch roles for the Roles tab // Fetch user roles for the dropdown
        fetchActiveUsers(); // Fetch active users for the Users tab
    }, []);

    const checkSimilarRole = useCallback(async (name) => {
        if (!name || name.trim().length < 2) { setSimilarRoles([]); return; }
        try {
            const res = await apiFetch(`/api/Permissions/check-similar-role?name=${encodeURIComponent(name.trim())}`);
            const data = await res.json();
            if (data.success) setSimilarRoles(data.data ?? []);
        } catch { /* silent */ }
    }, [apiFetch]);

    const handleAddRole = async () => {  //functionality for adding a role
        if (newRoleName.trim() === '') {
            toast.error("Role name cannot be empty!");
            return;
        }

        try {
            setIsLoading(true)
            const response = await apiFetch('/api/Permissions/add', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ roleName: newRoleName })
            });

            const data = await response.json();

            if (data.success) {
                toast.success(`Role "${newRoleName}" added successfully!`);
                setNewRoleName('');
                closeModal();
                fetchRoles();
            } else {
                toast.error(data.message || "Failed to add role.");
            }

        } catch (e) {
            Sentry.captureException(e);
            if (e.message && e.message.includes('Session expired')) {
                setSessionExpired(true);
                logout();
                return;
            }
            console.error(e);
            toast.error("An error occurred while adding the role.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteRole = async (roleId, roleName) => {
        if (window.confirm(`Are you sure you want to delete the role "${roleName}"?`)) {
            try {
                setIsLoading(true);
                const response = await apiFetch(`/api/Permissions/delete/${roleId}`, {
                    method: "DELETE",
                });

                const data = await response.json()

                if (data.success) {
                    toast.success(`Role "${roleName}" deleted successfully!`);
                    fetchRoles(); // Refetch roles after deletion
                    if (selectedRole === roleId) {
                        setSelectedRole(null); // Clear selection if deleted role was selected
                    }
                } else {
                    const errorData = await response.json();
                    toast.error(errorData.message || "Failed to delete role.");
                }
            } catch (e) {
                Sentry.captureException(e);
                if (e.message && e.message.includes('Session expired')) {
                    setSessionExpired(true);
                    logout();
                    return;
                }
                console.error(e);
                toast.error("An error occurred while deleting the role.");
            } finally {
                setIsLoading(false);
            }
        }
    };

    const calculateRoleCounts = () => {
        const counts = {};
        users.forEach(user => {
            if (user.roleID) {
                counts[user.roleID] = (counts[user.roleID] || 0) + 1;
            }
        });
        setRoleCounts(counts);
    };

    useEffect(() => {
        calculateRoleCounts();
    }, [users]);

    const loadUserPermissionsData = async () => {
        try {
            setIsLoading(true);

            await fetchActiveUsers();

            const userToProcessResponse = await apiFetch("/api/Permissions/all-user-processes");
            if (userToProcessResponse.ok) {
                const data = await userToProcessResponse.json();
                if (data.success) {
                    setUserToProcessMappings(data.data);
                }
            } else {
                toast.error("Failed to fetch user-to-process mappings");
            }

            const roleProcessesResponse = await apiFetch("/api/Permissions/all-role-processes");
            if (roleProcessesResponse.ok) {
                const roleProcessesData = await roleProcessesResponse.json();
                if (roleProcessesData.success) {
                    setRoleProcessesMappings(roleProcessesData.data);
                }
            } else {
                toast.error("Failed to fetch role-to-process mappings");
            }

            await loadProcessesWithoutRole();
        } catch (error) {
            Sentry.captureException(error);
            if (error.message && error.message.includes('Session expired')) {
                setSessionExpired(true);
                logout();
                return;
            }
            console.error("Error loading user permissions data:", error);
            toast.error("An error occurred while loading user permissions data");
        } finally {
            setIsLoading(false);
        }
    };

    const handleTabClick = (tab) => {
        if (activeTab === "roles" && tab !== "roles") {
            setSelectedRole(null);
            setProcesses([]);
        }

        if (tab === "permissions") {
            setIsLoading(true);
            setSelectedUser(null);
            setProcesses([]);

            loadUserPermissionsData().then(() => {
                setIsLoading(false);
            });
        }

        setActiveTab(tab);
    };

    const handleUserClick = (userID) => {
        setSelectedUser(userID);

        // Get the selected user and their role
        const user = users.find(u => u.userID === userID);

        // Start with fresh copy of all processes
        let processesWithPermissions = processes.map(process => ({
            ...process,
            grantAccess: false,
            enquiryOnly: false,
            fromRole: false,
            fromUser: false
        }));

        // Apply role permissions if the user has a role
        if (user && user.roleID) {
            const roleProcesses = roleProcessesMappings.filter(rp => rp.roleID === user.roleID);

            processesWithPermissions = processesWithPermissions.map(process => {
                const roleProcess = roleProcesses.find(rp => rp.processID === process.processID);
                if (roleProcess) {
                    return {
                        ...process,
                        grantAccess: true,
                        enquiryOnly: roleProcess.enquiryOnly,
                        fromRole: true
                    };
                }
                return process;
            });
        }

        // Apply user-specific permissions
        const userProcesses = userToProcessMappings.filter(up => up.userID === userID);

        processesWithPermissions = processesWithPermissions.map(process => {
            const userProcess = userProcesses.find(up => up.processID === process.processID);
            if (userProcess) {
                return {
                    ...process,
                    grantAccess: true,
                    enquiryOnly: userProcess.enquiryOnly,
                    fromUser: true
                };
            }
            return process;
        });

        setProcesses(processesWithPermissions);
    };

    const loadProcessesWithoutRole = async () => {
        try {
            const processesResponse = await apiFetch("/api/Permissions/processes");
            if (!processesResponse.ok) {
                toast.error("Failed to fetch processes");
                return;
            }
            const processesData = await processesResponse.json();

            // Initialize all processes with no permissions
            const processesWithNoPermissions = processesData.data.map(process => ({
                ...process,
                grantAccess: false,
                enquiryOnly: false
            }));

            setProcesses(processesWithNoPermissions);
        } catch (error) {
            Sentry.captureException(error);
            if (error.message && error.message.includes('Session expired')) {
                setSessionExpired(true);
                logout();
                return;
            }
            console.error("Error loading processes:", error);
        }
    };

    //Functions for opening and closing the modal
    const openModal = () => {
        setSimilarRoles([]);
        setNewRoleName('');
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSimilarRoles([]);
    };

    //Functions for role searching
    const filteredRoles = roles.filter(role =>
        role.roleName.toLowerCase().includes(searchRolesTerm.toLowerCase())
    );

    const handleRolesSearch = (e) => {
        setSearchRolesTerm(e.target.value);
    };

    //Functions for process searching
    const handleProcessesSearch = (e) => {
        setSearchProcessesTerm(e.target.value);
    };

    const handleUserPermissionChanges = (processID, granted, isRolePermission) => {
        if (isRolePermission) {
            setIsRoleChangeModalOpen(true);
        } else {
            updateProcessState(processID, granted, "grantAccess");
        }
    };

    const handleUserEnquiryOnlyChanges = (processID, enquiryOnly, isRolePermission) => {
        if (isRolePermission) {
            setIsRoleChangeModalOpen(true);
        } else {
            updateProcessState(processID, enquiryOnly, "enquiryOnly");
        }
    };

    const updateProcessState = (processID, value, field) => {
        setProcesses(prevProcesses =>
            prevProcesses.map(p =>
                p.processID === processID
                    ? { ...p, [field]: value, fromUser: true }
                    : p
            )
        );
    };

    const removeUserRole = async () => {
        if (!selectedUser) return;

        try {
            setIsLoading(true);

            const response = await apiFetch(`/api/Permissions/assign-custom-role/${selectedUser}`, {
                method: "PUT",
            });

            const data = await response.json();

            if (data.success) {
                toast.success("User has been assigned to the Custom role.");

                await fetchActiveUsers();
                await loadUserPermissionsData();

                setProcesses([]);

                setTimeout(() => {
                    if (selectedUser) {
                        const refreshedUser = users.find(u => u.userID === selectedUser);
                        if (refreshedUser) {
                            let processesWithPermissions = processes.map(process => ({
                                ...process,
                                grantAccess: false,
                                enquiryOnly: false,
                                fromRole: false,
                                fromUser: false
                            }));

                            const userProcesses = userToProcessMappings.filter(up => up.userID === selectedUser);

                            processesWithPermissions = processesWithPermissions.map(process => {
                                const userProcess = userProcesses.find(up => up.processID === process.processID);
                                if (userProcess) {
                                    return {
                                        ...process,
                                        grantAccess: true,
                                        enquiryOnly: userProcess.enquiryOnly,
                                        fromUser: true
                                    };
                                }
                                return process;
                            });

                            setProcesses(processesWithPermissions);
                        }
                    }
                }, 100);
            } else {
                toast.error("Failed to remove user role.");
            }
        } catch (e) {
            Sentry.captureException(e);
            if (e.message && e.message.includes('Session expired')) {
                setSessionExpired(true);
                logout();
                return;
            }
            console.error(e);
            toast.error("An error occurred while removing user role.");
        } finally {
            setIsLoading(false);
            setIsRoleChangeModalOpen(false);
        }
    };

    if (sessionExpired) {
        return (
            <div className="error-message">
                <p>Your session has expired. Please log in again.</p>
                <button onClick={() => navigate('/')}>Go to Login</button>
            </div>
        );
    }

    return (
        <div className="permissions-management">
            <h1>Permissions Management</h1>

            {/* Tabs for switching between Roles and User Permissions */}
            <div className="tabs horizontal-tabs">
                <div
                    className={`tab ${activeTab === "roles" ? "active" : ""}`}
                    onClick={() => handleTabClick("roles")}
                >
                    Manage Roles
                </div>
                <div
                    className={`tab ${activeTab === "permissions" ? "active" : ""}`}
                    onClick={() => handleTabClick("permissions")}
                >
                    User Permissions
                </div>
                <div
                    className={`tab ${activeTab === "users" ? "active" : ""}`}
                    onClick={() => handleTabClick("users")}
                >
                    Assign Roles
                </div>
            </div>

            {/* Roles Tab Layout */}
            {activeTab === "roles" && (
                <div className="roles-tab">
                    {/* Sidebar for Roles */}
                    <div className="sidebar">
                        <input
                            type="text"
                            placeholder="Search roles..."
                            className="search-input"
                            value={searchRolesTerm}
                            onChange={handleRolesSearch}
                        />
                        <ul className="roles-list">
                            {filteredRoles
                                .filter(role => role.roleName !== "Custom") // Filter out custom role only in this view
                                .map((role) => (
                                    <li
                                        key={role.roleID}
                                        className={`role-item ${selectedRole === role.roleID ? 'selected' : ''}`}
                                        onClick={() => handleRoleClick(role.roleID)}
                                    >
                                        <span className="role-name-container">
                                            {role.roleName}
                                            <span className="user-count">({rolesCount[role.roleID] || 0})</span>
                                        </span>
                                        <div className="role-actions">
                                            <button className="edit-button">Edit</button>
                                            <button className="delete-button" onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteRole(role.roleID, role.roleName);
                                            }}>Delete</button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                            <button style={{ marginTop: '1em' }} onClick={openModal}>Add Role</button>
                    </div>

                    {/* Main Content Area */}
                    <div className="content">
                        {/* Header with Global Enquiry Only and Search */}
                        <div className="header">
                            <h2>
                                {selectedRole
                                    ? `Permissions for ${roles.find(r => r.roleID === selectedRole)?.roleName}`
                                    : "Select a Role"}
                            </h2>
                            <input
                                type="text"
                                placeholder="Search permissions..."
                                className="search-input"
                                value={searchProcessesTerm}
                                onChange={handleProcessesSearch}
                            />
                        </div>
                        
                        {/* Action buttons row */}
                        {selectedRole && processes.length > 0 && (
                            <div className="permissions-actions-row">
                                <div className="actions-left">
                                    <button
                                        className="save-button"
                                        onClick={handleSave}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? "Saving..." : "Save Permissions"}
                                    </button>
                                    <button
                                        className="template-button"
                                        onClick={() => setIsTemplateModalOpen(true)}
                                    >
                                        📋 Apply Template
                                    </button>
                                </div>
                                <div className="actions-right">
                                    <button className="expand-collapse-btn" onClick={expandAllCategories}>
                                        ▼ Expand All
                                    </button>
                                    <button className="expand-collapse-btn" onClick={collapseAllCategories}>
                                        ▲ Collapse All
                                    </button>
                                </div>
                            </div>
                        )}
                        
                        {/* Categorized Permissions List */}
                        {selectedRole && processes.length > 0 && (
                            <div className="permissions-categories-container">
                                {Object.keys(filteredGroupedProcesses).map((category) => {
                                    const stats = getCategoryStats(category);
                                    const isExpanded = expandedCategories[category];
                                    const allSelected = isCategoryAllSelected(category);
                                    const someSelected = isCategorySomeSelected(category);
                                    
                                    return (
                                        <div key={category} className="permission-category">
                                            {/* Category Header */}
                                            <div className="category-header-row">
                                                <div 
                                                    className="category-toggle"
                                                    onClick={() => toggleCategory(category)}
                                                >
                                                    <span className="toggle-icon">{isExpanded ? '▼' : '▶'}</span>
                                                    <span className="category-name">{category}</span>
                                                    <span className="category-stats">
                                                        ({stats.selected}/{stats.total} selected
                                                        {stats.enquiryOnly > 0 && `, ${stats.enquiryOnly} enquiry-only`})
                                                    </span>
                                                </div>
                                                <div className="category-actions">
                                                    <label className="select-all-label">
                                                        <input
                                                            type="checkbox"
                                                            checked={allSelected}
                                                            ref={input => {
                                                                if (input) input.indeterminate = someSelected;
                                                            }}
                                                            onChange={(e) => selectAllInCategory(category, e.target.checked)}
                                                        />
                                                        Select All
                                                    </label>
                                                    <label className="enquiry-all-label">
                                                        <input
                                                            type="checkbox"
                                                            checked={stats.selected > 0 && stats.enquiryOnly === stats.selected}
                                                            onChange={(e) => setEnquiryOnlyInCategory(category, e.target.checked)}
                                                            disabled={stats.selected === 0}
                                                        />
                                                        All Enquiry-Only
                                                    </label>
                                                </div>
                                            </div>
                                            
                                            {/* Category Items */}
                                            {isExpanded && (
                                                <div className="category-items-list">
                                                    <table className="permissions-table category-table">
                                                        <thead>
                                                            <tr>
                                                                <th>Permission</th>
                                                                <th style={{ width: '120px', textAlign: 'center' }}>Grant Access</th>
                                                                <th style={{ width: '120px', textAlign: 'center' }}>Enquiry Only</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {filteredGroupedProcesses[category].map((process) => (
                                                                <tr key={process.processID} className={process.grantAccess ? 'granted' : ''}>
                                                                    <td>
                                                                        <span
                                                                            title={process.tooltip || process.description}
                                                                            style={{ cursor: 'help' }}
                                                                        >
                                                                            {process.description}
                                                                        </span>
                                                                        {process.pageRoute && !process.pageRoute.includes(':') && (
                                                                            <a
                                                                                href={process.pageRoute}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                title={`Open ${process.description}`}
                                                                                style={{ marginLeft: '6px', fontSize: '0.8em', opacity: 0.7, textDecoration: 'none' }}
                                                                                onClick={(e) => e.stopPropagation()}
                                                                            >
                                                                                ↗
                                                                            </a>
                                                                        )}
                                                                    </td>
                                                                    <td style={{ textAlign: 'center' }}>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={process.grantAccess}
                                                                            onChange={(e) => handleRoleProcessChange(
                                                                                process.processID,
                                                                                "grantAccess",
                                                                                e.target.checked
                                                                            )}
                                                                        />
                                                                    </td>
                                                                    <td style={{ textAlign: 'center' }}>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={process.enquiryOnly}
                                                                            onChange={(e) => handleRoleProcessChange(
                                                                                process.processID,
                                                                                "enquiryOnly",
                                                                                e.target.checked
                                                                            )}
                                                                            disabled={!process.grantAccess}
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* User Permissions Tab Layout - Enhanced with category grouping and inheritance visual */}
            {activeTab === "permissions" && (
                <div className="permissions-tab">
                    <div className="roles-tab">
                        {/* Sidebar for Users */}
                        <div className="sidebar">
                            <input
                                type="text"
                                placeholder="Search users..."
                                className="search-input"
                                value={searchUsersTerm}
                                onChange={(e) => setSearchUsersTerm(e.target.value)}
                            />
                            <ul className="roles-list">
                                {users
                                    .filter(user =>
                                        (`${user.firstName} ${user.lastName}`).toLowerCase().includes(searchUsersTerm.toLowerCase())
                                    )
                                    .map((user) => (
                                        <li
                                            key={user.userID}
                                            className={`role-item ${selectedUser === user.userID ? 'selected' : ''}`}
                                            onClick={() => handleUserClick(user.userID)}
                                        >
                                            <span className="role-name-container">
                                                {user.firstName} {user.lastName}
                                                {user.roleID > 0 && (
                                                    <span className="role-info">
                                                        ({roles.find(r => r.roleID === user.roleID)?.roleName || 'No Role'})
                                                    </span>
                                                )}
                                            </span>
                                        </li>
                                    ))}
                            </ul>
                        </div>

                        {/* Main Content Area */}
                        <div className="content">
                            {/* Header with Search */}
                            <div className="header">
                                <h2>
                                    {selectedUser
                                        ? `Permissions for ${users.find(u => u.userID === selectedUser)?.firstName} ${users.find(u => u.userID === selectedUser)?.lastName}`
                                        : "Select a User"}
                                </h2>
                                <input
                                    type="text"
                                    placeholder="Search permissions..."
                                    className="search-input"
                                    value={searchUserProcessesTerm}
                                    onChange={(e) => setSearchUserProcessesTerm(e.target.value)}
                                />
                            </div>
                            
                            {/* Legend for permission source */}
                            {selectedUser && processes.length > 0 && (
                                <div className="permissions-legend">
                                    <div className="legend-item">
                                        <span className="legend-icon role-inherited">●</span>
                                        <span>Inherited from Role (click to customize)</span>
                                    </div>
                                    <div className="legend-item">
                                        <span className="legend-icon user-specific">●</span>
                                        <span>User-Specific Permission</span>
                                    </div>
                                </div>
                            )}
                            
                            {/* Action buttons */}
                            {selectedUser && processes.length > 0 && (
                                <div className="permissions-actions-row">
                                    <div className="actions-left">
                                        <button
                                            className="save-button"
                                            onClick={handleSaveUserPermissions}
                                            disabled={isLoading}
                                        >
                                            {isLoading ? "Saving..." : "Save User Permissions"}
                                        </button>
                                    </div>
                                    <div className="actions-right">
                                        <button className="expand-collapse-btn" onClick={expandAllCategories}>
                                            ▼ Expand All
                                        </button>
                                        <button className="expand-collapse-btn" onClick={collapseAllCategories}>
                                            ▲ Collapse All
                                        </button>
                                    </div>
                                </div>
                            )}
                            
                            {/* Categorized User Permissions with visual distinction */}
                            {selectedUser && processes.length > 0 && (
                                <div className="permissions-categories-container">
                                    {Object.keys(filteredUserGroupedProcesses).map((category) => {
                                        const stats = getCategoryStats(category);
                                        const isExpanded = expandedCategories[category];
                                        
                                        return (
                                            <div key={category} className="permission-category">
                                                {/* Category Header */}
                                                <div className="category-header-row">
                                                    <div 
                                                        className="category-toggle"
                                                        onClick={() => toggleCategory(category)}
                                                    >
                                                        <span className="toggle-icon">{isExpanded ? '▼' : '▶'}</span>
                                                        <span className="category-name">{category}</span>
                                                        <span className="category-stats">
                                                            ({stats.selected}/{stats.total} selected)
                                                        </span>
                                                    </div>
                                                </div>
                                                
                                                {/* Category Items */}
                                                {isExpanded && (
                                                    <div className="category-items-list">
                                                        <table className="permissions-table category-table user-permissions-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Permission</th>
                                                                    <th style={{ width: '100px', textAlign: 'center' }}>Source</th>
                                                                    <th style={{ width: '120px', textAlign: 'center' }}>Grant Access</th>
                                                                    <th style={{ width: '120px', textAlign: 'center' }}>Enquiry Only</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {filteredUserGroupedProcesses[category].map((process) => {
                                                                    const isRolePermission = process.fromRole && !process.fromUser;
                                                                    const isUserPermission = process.fromUser;
                                                                    
                                                                    return (
                                                                        <tr 
                                                                                key={process.processID} 
                                                                                className={`
                                                                                    ${process.grantAccess ? 'granted' : ''}
                                                                                    ${isRolePermission ? 'role-inherited' : ''}
                                                                                    ${isUserPermission ? 'user-specific' : ''}
                                                                                `}
                                                                            >
                                                                                <td>
                                                                                    <span
                                                                                        title={process.tooltip || process.description}
                                                                                        style={{ cursor: 'help' }}
                                                                                    >
                                                                                        {process.description}
                                                                                    </span>
                                                                                    {process.pageRoute && !process.pageRoute.includes(':') && (
                                                                                        <a
                                                                                            href={process.pageRoute}
                                                                                            target="_blank"
                                                                                            rel="noopener noreferrer"
                                                                                            title={`Open ${process.description}`}
                                                                                            style={{ marginLeft: '6px', fontSize: '0.8em', opacity: 0.7, textDecoration: 'none' }}
                                                                                            onClick={(e) => e.stopPropagation()}
                                                                                        >
                                                                                            ↗
                                                                                        </a>
                                                                                    )}
                                                                                </td>
                                                                                <td style={{ textAlign: 'center' }}>
                                                                                    {isRolePermission && (
                                                                                        <span className="source-badge role-badge" title="Inherited from assigned role">
                                                                                            👥 Role
                                                                                        </span>
                                                                                    )}
                                                                                    {isUserPermission && (
                                                                                        <span className="source-badge user-badge" title="User-specific permission">
                                                                                            👤 User
                                                                                        </span>
                                                                                    )}
                                                                                    {!isRolePermission && !isUserPermission && process.grantAccess && (
                                                                                        <span className="source-badge" title="Direct assignment">
                                                                                            —
                                                                                        </span>
                                                                                    )}
                                                                                </td>
                                                                                <td style={{ textAlign: 'center' }}>
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={process.grantAccess}
                                                                                        onChange={(e) => handleUserPermissionChanges(process.processID, e.target.checked, isRolePermission)}
                                                                                        className={isRolePermission ? 'role-inherited-checkbox' : ''}
                                                                                    />
                                                                                </td>
                                                                                <td style={{ textAlign: 'center' }}>
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={process.enquiryOnly}
                                                                                        onChange={(e) => handleUserEnquiryOnlyChanges(process.processID, e.target.checked, isRolePermission)}
                                                                                        disabled={!process.grantAccess}
                                                                                        className={isRolePermission ? 'role-inherited-checkbox' : ''}
                                                                                    />
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Manage Users Tab Layout */}
            {activeTab === "users" && (
                <div className="users-tab">
                    <h2>Manage Users</h2>
                    <table className="users-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Role</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user) => (
                                <tr key={user.userID}>
                                    <td>{`${user.firstName} ${user.lastName}`}</td>
                                    <td>
                                        <select
                                            value={user.roleID == null ? 0 : user.roleID}
                                            onChange={(e) =>
                                                updateUserRole(user.userID, parseInt(e.target.value))
                                            }
                                        >
                                            <option value={0}>No Role</option>
                                            {roles.map((role) => (
                                                <option key={role.roleID} value={role.roleID}>
                                                    {role.roleName}
                                                </option>
                                            ))}
                                        </select>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Add Role Modal */}
            {activeTab === "roles" && isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h2>Add Role</h2>
                        <input
                            type="text"
                            placeholder="Enter role name..."
                            value={newRoleName}
                            onChange={(e) => {
                                setNewRoleName(e.target.value);
                                clearTimeout(roleDebounceRef.current);
                                roleDebounceRef.current = setTimeout(() => checkSimilarRole(e.target.value), 500);
                            }}
                            onBlur={() => checkSimilarRole(newRoleName)}
                            style={similarRoles.some(r => r.isExact) ? { borderColor: '#ef4444' } : similarRoles.length > 0 ? { borderColor: '#f59e0b' } : undefined}
                        />
                        {similarRoles.length > 0 && (
                            <div style={{
                                marginTop: 8,
                                padding: '8px 12px',
                                borderRadius: 7,
                                background: similarRoles.some(r => r.isExact) ? '#fef2f2' : '#fffbeb',
                                border: `1px solid ${similarRoles.some(r => r.isExact) ? '#fecaca' : '#fde68a'}`,
                                fontSize: '0.82rem',
                                color: similarRoles.some(r => r.isExact) ? '#991b1b' : '#92400e'
                            }}>
                                <FaExclamationTriangle style={{ marginRight: 6 }} />
                                {similarRoles.some(r => r.isExact)
                                    ? 'A role with this exact name already exists:'
                                    : 'Similar role names already exist — please confirm:'}
                                <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                                    {similarRoles.map(r => (
                                        <li key={r.roleID}>
                                            <strong>{r.roleName}</strong>
                                            {r.isExact && <span style={{ marginLeft: 6, color: '#ef4444', fontWeight: 700 }}>(exact match)</span>}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <div className="modal-actions">
                            <button onClick={closeModal} className="button-secondary">
                                Cancel
                            </button>
                            <button disabled={isLoading || similarRoles.some(r => r.isExact)} onClick={handleAddRole} className="save-button">
                                Save
                            </button>                            
                        </div>
                    </div>
                </div>
            )}

            {isRoleChangeModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h2>Confirm Role Removal</h2>
                        <p>
                            Making this change will remove the role from this user. All permissions for this user will need to be assigned manually using this page. Do you want to proceed?
                        </p>
                        <div className="modal-actions">
                            <button
                                onClick={removeUserRole}
                                className="confirm-button"
                                disabled={isLoading}
                            >
                                {isLoading ? "Processing..." : "Yes, Proceed"}
                            </button>
                            <button
                                onClick={() => {
                                    setIsRoleChangeModalOpen(false);
                                }}
                                className="cancel-button"
                                disabled={isLoading}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Role Templates Modal */}
            {isTemplateModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content template-modal">
                        <h2>📋 Apply Permission Template</h2>
                        <p className="template-description">
                            Select a template to quickly configure permissions for this role. 
                            Changes will be previewed - remember to save after applying.
                        </p>
                        <div className="template-list">
                            {roleTemplates.map((template, index) => (
                                <div 
                                    key={index} 
                                    className="template-item"
                                    onClick={() => applyRoleTemplate(template)}
                                >
                                    <span className="template-icon">{template.icon}</span>
                                    <div className="template-info">
                                        <span className="template-name">{template.name}</span>
                                        <span className="template-desc">{template.description}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="modal-actions">
                            <button
                                onClick={() => setIsTemplateModalOpen(false)}
                                className="button-secondary"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}


        </div>
    );
};

export default UserPermissionsPage;